import {
  SiteDemandView,
  useDemandData,
  useWarm,
  type AITrace,
  type HhDataItem,
  type MultiCurveCandidate,
} from "@inrange/building-manager-api-client";
import { reversed, sortBy } from "@inrange/utils";
import {
  ChangeEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, Card, Col, Container, Form, Row, Spinner } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import Loading from "../Loading";
import DemandDataLoadSiteActuals from "./DemandDataLoadSiteActuals";
import DemandDataSiteAssignment from "./DemandDataSiteAssignment";
import DemandDataSiteRemoval from "./DemandDataSiteRemoval";
import DemandFileAnalysisReviewModal from "./DemandFileAnalysisReviewModal";
import DemandFileConfigModal from "./DemandFileConfigModal";
import DemandFileProcessModal from "./DemandFileProcessModal";
import MatchToSitesModal from "./MatchToSitesModal";
import MultiCurveSplitModal from "./MultiCurveSplitModal";
import { HhDataTempFile } from "./DemandFileSummaryRow";
import FileSummaryTable from "./DemandFileSummaryTable";
import GraphDemandData from "./GraphDemandData";
import HhDataTable from "./HhDataTable";

const DemandData = () => {
  useWarm("demand");
  document.title = "Demand Data";

  const [searchParams, setSearchParams] = useSearchParams();
  const initialFilterText = searchParams.get("search") || "";
  const viewConfigGuid = searchParams.get("viewConfig") || "";

  const {
    fetchHhDataTempList,
    fetchHhDataList,
    fetchHhDataTempConfig,
    uploadHhDataTemp,
    deleteHhDataTempFile,
    commitHhDataTemp,
    editHhData,
    deleteHhDataFile,
    downloadHhDataZip,
    processHhDataTemp,
    processHhDataTempAuto,
    pollAutoOperationStatuses,
    fetchHhDataTempErrorAnalysis,
  } = useDemandData();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadInProgress, setUploadInProgress] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{
    current: number;
    total: number;
  } | null>(null);
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [selectedFileToProcess, setSelectedFileToProcess] =
    useState<HhDataTempFile | null>(null);
  const [config, setConfig] = useState<any>({});
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [selectedHhDataItem, setSelectedHhDataItem] =
    useState<HhDataItem | null>(null);
  // Committed file whose AI analysis the operator is reviewing (read-only modal).
  const [reviewAnalysisItem, setReviewAnalysisItem] =
    useState<HhDataItem | null>(null);
  const [selectedTempFiles, setSelectedTempFiles] = useState<HhDataTempFile[]>(
    []
  );
  const [selectedHhDataItems, setSelectedHhDataItems] = useState<HhDataItem[]>(
    []
  );
  const [showMatchToSitesModal, setShowMatchToSitesModal] = useState(false);
  // Auto-process bookkeeping. Persistence lives server-side via a per-file S3 marker
  // (lastAutoOperationKey on each row from /hh_data/temp), so any operator loading
  // this page sees the same row-level status. These local Sets/Maps are derived from
  // (or kicked off into) that server-side state and tracked here for in-render speed
  // and for the polling effect's transient state.
  const [autoProcessingGuids, setAutoProcessingGuids] = useState<Set<string>>(
    new Set()
  );
  const [autoProcessingTotal, setAutoProcessingTotal] = useState(0);
  // operationKey per fileGuid - seeded from the listing's lastAutoOperationKey AND
  // updated immediately when the operator kicks off a fresh bulk run (before the
  // listing refetch lands the BE-stored value).
  const [autoOperationKeys, setAutoOperationKeys] = useState<
    Map<string, string>
  >(new Map());
  // Guids whose LRO returned status='failed'. Updated from polling results; cleared
  // on a bulk re-fire for the same guid. Persistence comes from the listing's
  // lastAutoOperationKey - on reload we detect "key set + no outputMetaData + /async_status
  // says failed" and re-populate this from the polling effect.
  const [autoFailedGuids, setAutoFailedGuids] = useState<Set<string>>(new Set());
  // Guids for which the AI auto-fix analysis is currently in flight (subset of
  // autoFailedGuids while analysis runs). When analysis returns it gets cleared.
  const [autoAnalysingGuids, setAutoAnalysingGuids] = useState<Set<string>>(
    new Set()
  );
  // Guids whose AI analysis returned a proposed_config that we auto-applied via
  // /process - waiting on the reprocess LRO. Subset of autoFailedGuids while
  // the reprocess is in flight; cleared on its completion (success removes from
  // autoFailedGuids too; failure leaves autoFailedGuids set to break re-loop).
  const [autoReprocessingGuids, setAutoReprocessingGuids] = useState<Set<string>>(
    new Set()
  );
  // Cached AI analysis result per fileGuid. Lets the row's [Review file analysis]
  // button open the modal with the analysis already loaded (instant, no re-fetch),
  // and lets [Reprocess with proposed fix] fire /process without re-fetching.
  type AnalysisResult = {
    summary: string;
    details: string;
    proposed_config?: Record<string, unknown> | null;
    proposed_config_rationale?: string | null;
    trace?: AITrace | null;
  };
  const [autoAnalysisResults, setAutoAnalysisResults] = useState<
    Map<string, AnalysisResult>
  >(new Map());
  // Guids where we've already auto-applied an AI fix at least once this invocation.
  // After the first auto-fix fails we re-analyse (history-aware) but DON'T auto-apply
  // again - the updated fix is surfaced for the operator to apply manually. This both
  // keeps the operator in control and bounds the auto-fix loop.
  const [autoFixAttemptedGuids, setAutoFixAttemptedGuids] = useState<Set<string>>(
    new Set()
  );
  // Guids that were successfully processed via an AI-proposed fix (the auto-fix
  // reprocess completed). Drives the green "Auto-fixed with AI" badge.
  const [autoFixedGuids, setAutoFixedGuids] = useState<Set<string>>(new Set());
  // Per-file count of processing attempts this invocation (from the server-side
  // attempt history). Shown as "(attempt N)" on the badge - informational only,
  // we never enforce a bound.
  const [autoAttemptCounts, setAutoAttemptCounts] = useState<
    Map<string, number>
  >(new Map());
  // Guids that /process_auto flagged as multi-curve (one input file holding several
  // demand curves). These are NOT processed - they wait for the operator to confirm
  // the per-curve split via the MultiCurveSplitModal. The candidates map holds the
  // detector's proposed per-curve configs so the Review button can open the modal
  // without re-detecting.
  const [autoMultiCurveGuids, setAutoMultiCurveGuids] = useState<Set<string>>(
    new Set()
  );
  const [autoMultiCurveCandidates, setAutoMultiCurveCandidates] = useState<
    Map<string, { candidates: MultiCurveCandidate[]; reason: string }>
  >(new Map());
  // The file whose split-review modal is currently open (null = closed).
  const [splitModalFile, setSplitModalFile] = useState<HhDataTempFile | null>(
    null
  );
  // True while a bulk "Delete N files" action is deleting the selected files.
  const [bulkDeleteInProgress, setBulkDeleteInProgress] = useState(false);
  // operationKey passed into DemandFileProcessModal when the operator opens Process on a
  // previously-failed bulk-Auto row. Null for any other entry-point so the modal doesn't
  // re-poll a long-completed LRO.
  const [bulkFailedOperationKey, setBulkFailedOperationKey] = useState<
    string | null
  >(null);
  // Cached analysis handed to the modal when the operator clicks [Review file
  // analysis], so the modal renders it instantly instead of re-running the AI.
  const [reviewAnalysis, setReviewAnalysis] = useState<AnalysisResult | null>(
    null
  );

  // Hydrate local Auto-process state from the listing's lastAutoOperationKey. Any
  // file that has a stored operationKey + no outputMetaData needs a /async_status
  // check to figure out if it's still running or has failed. We add such guids to
  // autoProcessingGuids; the polling effect then resolves their actual status.
  // Idempotent across refetches - we skip guids already tracked locally.
  useEffect(() => {
    const files = (fetchHhDataTempList.data?.files || []) as unknown as HhDataTempFile[];
    if (files.length === 0) return;
    const newOperationKeys = new Map(autoOperationKeys);
    const newProcessingGuids = new Set(autoProcessingGuids);
    const newFailedGuids = new Set(autoFailedGuids);
    let opKeysChanged = false;
    let procChanged = false;
    let failedChanged = false;
    for (const file of files) {
      const opKey = file.lastAutoOperationKey;
      if (!opKey) continue;
      if (newOperationKeys.get(file.fileGuid) !== opKey) {
        newOperationKeys.set(file.fileGuid, opKey);
        opKeysChanged = true;
      }
      // A file still missing outputMetaData that we're not already tracking locally
      // needs its status restored. Distinguish two cases so a previously-FAILED row
      // doesn't flash "Auto-processing..." for the second it takes /async_status to
      // resolve on reload:
      //   - It has terminal attempt history (autoAttemptCount > 0) or a cached
      //     analysis => it already ran and failed (a success would have produced
      //     outputMetaData), so restore it straight into the failed set.
      //   - Otherwise no terminal attempt has landed yet => it's genuinely in flight,
      //     so mark it processing and let the polling effect resolve it.
      if (
        !file.outputMetaData &&
        !autoProcessingGuids.has(file.fileGuid) &&
        !autoFailedGuids.has(file.fileGuid)
      ) {
        const alreadyFailed =
          (file.autoAttemptCount ?? 0) > 0 || !!file.latestAnalysis;
        if (alreadyFailed) {
          newFailedGuids.add(file.fileGuid);
          failedChanged = true;
        } else {
          newProcessingGuids.add(file.fileGuid);
          procChanged = true;
        }
      }
    }
    if (opKeysChanged) setAutoOperationKeys(newOperationKeys);
    if (failedChanged) setAutoFailedGuids(newFailedGuids);
    if (procChanged) {
      setAutoProcessingGuids(newProcessingGuids);
      // Preserve "X of N" sensibility on reload - if total is 0 but we just
      // discovered in-flight guids, bump total to match. New in-session bulk runs
      // overwrite this from handleAutoProcessSelected.
      setAutoProcessingTotal((prev) =>
        prev > 0 ? prev : newProcessingGuids.size
      );
    }

    // Restore the AI-assist state from the server-derived listing fields so the
    // badges, attempt counter, and cached analysis survive reloads and show for any
    // operator. Don't clobber a fresher in-session analysis result if we already
    // have one locally (the listing may lag the live LRO by a poll).
    setAutoAttemptCounts((prev) => {
      const next = new Map(prev);
      for (const file of files) {
        if (typeof file.autoAttemptCount === "number") {
          next.set(file.fileGuid, file.autoAttemptCount);
        }
      }
      return next;
    });
    setAutoFixAttemptedGuids((prev) => {
      const next = new Set(prev);
      for (const file of files) {
        if (file.autoFixAttempted) next.add(file.fileGuid);
      }
      return next;
    });
    setAutoFixedGuids((prev) => {
      const next = new Set(prev);
      for (const file of files) {
        if (file.autoFixed) next.add(file.fileGuid);
      }
      return next;
    });
    setAutoAnalysisResults((prev) => {
      const next = new Map(prev);
      for (const file of files) {
        if (file.latestAnalysis && !next.has(file.fileGuid)) {
          next.set(file.fileGuid, {
            summary: file.latestAnalysis.summary ?? "",
            details: file.latestAnalysis.details ?? "",
            proposed_config: file.latestAnalysis.proposed_config ?? null,
            proposed_config_rationale:
              file.latestAnalysis.proposed_config_rationale ?? null,
            trace: file.latestAnalysis.trace ?? null,
          });
        }
      }
      return next;
    });
    // Rehydrate the parked multi-curve "review split" state from the persisted
    // multi_curve_detection.json marker (surfaced on the listing). The server only
    // includes it for un-split files (no outputMetaData), so this is safe to apply
    // verbatim. Don't clobber a fresher in-session entry.
    setAutoMultiCurveGuids((prev) => {
      const next = new Set(prev);
      for (const file of files) {
        if (file.multiCurveCandidates && file.multiCurveCandidates.length > 0) {
          next.add(file.fileGuid);
        }
      }
      return next;
    });
    setAutoMultiCurveCandidates((prev) => {
      const next = new Map(prev);
      for (const file of files) {
        if (
          file.multiCurveCandidates &&
          file.multiCurveCandidates.length > 0 &&
          !next.has(file.fileGuid)
        ) {
          next.set(file.fileGuid, {
            candidates: file.multiCurveCandidates,
            reason: file.multiCurveReason ?? "",
          });
        }
      }
      return next;
    });
  }, [fetchHhDataTempList.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFiles([...selectedFiles, ...Array.from(e.target.files)]);
      // Reset the input so the same file can be re-added if the operator removes it then adds it back
      e.target.value = "";
    }
  };

  const handleRemoveSelectedFile = (index: number) => {
    setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
  };

  const handleUpload = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (selectedFiles.length === 0) return;

    setUploadInProgress(true);
    setUploadProgress({ current: 0, total: selectedFiles.length });
    try {
      for (let i = 0; i < selectedFiles.length; i++) {
        setUploadProgress({ current: i, total: selectedFiles.length });
        await uploadHhDataTemp.mutateAsync({ file: selectedFiles[i] });
      }
      setSelectedFiles([]);
    } catch (error) {
      alert(`Error uploading file: ${error}`);
    } finally {
      setUploadInProgress(false);
      setUploadProgress(null);
    }
  };

  const handleAutoProcessSelected = async () => {
    const unprocessed = selectedTempFiles.filter((f) => !f.outputMetaData);
    if (unprocessed.length === 0) return;

    const guids = unprocessed.map((f) => f.fileGuid);
    setAutoProcessingGuids(new Set(guids));
    setAutoProcessingTotal(guids.length);
    // A fresh Auto-process starts from a clean slate for these guids (mirrors the
    // BE reset of attempt_history): clear the failed badge, any prior analysis
    // result, the fix-attempted flag, and the auto-fixed flag so the row tracks
    // this new invocation only.
    const clearGuidsFromSet = (s: Set<string>) => {
      const next = new Set(s);
      for (const g of guids) next.delete(g);
      return next;
    };
    setAutoFailedGuids(clearGuidsFromSet);
    setAutoAnalysingGuids(clearGuidsFromSet);
    setAutoReprocessingGuids(clearGuidsFromSet);
    setAutoFixAttemptedGuids(clearGuidsFromSet);
    setAutoFixedGuids(clearGuidsFromSet);
    setAutoMultiCurveGuids(clearGuidsFromSet);
    setAutoAnalysisResults((prev) => {
      const next = new Map(prev);
      for (const g of guids) next.delete(g);
      return next;
    });
    setAutoMultiCurveCandidates((prev) => {
      const next = new Map(prev);
      for (const g of guids) next.delete(g);
      return next;
    });

    const results = await Promise.allSettled(
      unprocessed.map((f) => processHhDataTempAuto.mutateAsync(f.fileGuid))
    );

    // A file detected as multi-curve isn't processed - park it for split review.
    const multiCurve = results
      .map((r, i) =>
        r.status === "fulfilled" &&
        r.value.status === "multi_curve_detected" &&
        r.value.candidates
          ? { file: unprocessed[i], result: r.value }
          : null
      )
      .filter((x): x is NonNullable<typeof x> => x !== null);
    if (multiCurve.length > 0) {
      setAutoMultiCurveGuids((prev) => {
        const next = new Set(prev);
        for (const m of multiCurve) next.add(m.file.fileGuid);
        return next;
      });
      setAutoMultiCurveCandidates((prev) => {
        const next = new Map(prev);
        for (const m of multiCurve) {
          next.set(m.file.fileGuid, {
            candidates: m.result.candidates!,
            reason: m.result.reason ?? "",
          });
        }
        return next;
      });
      // Multi-curve files never entered processing - drop them from the spinner set.
      setAutoProcessingGuids((prev) => {
        const next = new Set(prev);
        for (const m of multiCurve) next.delete(m.file.fileGuid);
        return next;
      });
    }

    // Record operationKeys for successful single-curve kickoffs so /async_status
    // polling can find them. Multi-curve responses carry no operationKey.
    setAutoOperationKeys((prev) => {
      const next = new Map(prev);
      results.forEach((r, i) => {
        if (r.status === "fulfilled" && r.value.operationKey) {
          next.set(unprocessed[i].fileGuid, r.value.operationKey);
        }
      });
      return next;
    });

    const kickoffFailures = results
      .map((r, i) => (r.status === "rejected" ? unprocessed[i] : null))
      .filter((f): f is HhDataTempFile => f !== null);

    if (kickoffFailures.length > 0) {
      // Drop kickoff failures from the polling set so we don't wait forever for them, and
      // mark them as failed so the row badge surfaces the issue.
      setAutoProcessingGuids((prev) => {
        const next = new Set(prev);
        for (const f of kickoffFailures) next.delete(f.fileGuid);
        return next;
      });
      setAutoFailedGuids((prev) => {
        const next = new Set(prev);
        for (const f of kickoffFailures) next.add(f.fileGuid);
        return next;
      });
      alert(
        `Failed to start auto-processing for ${kickoffFailures.length} file(s):\n` +
          kickoffFailures.map((f) => `- ${f.fileName}`).join("\n")
      );
    }
  };

  // Fires the AI auto-fix analysis for a freshly-failed file and, if the analysis
  // returns a proposed_config, automatically applies it via /process. On the happy
  // path the operator never has to click anything - the row goes
  //   Auto-processing... -> AI analysis running -> auto-fix applied, reprocessing -> healed.
  // If the analysis returns without a proposed_config (or the analysis itself fails),
  // the row settles to the "issue detected" state with [Review file analysis] +
  // [Reprocess with proposed fix] buttons.
  const fireAiAnalysisAndAutoApply = useCallback(
    async (fileGuid: string, logPayload: unknown) => {
      try {
        const result = await fetchHhDataTempErrorAnalysis.mutateAsync({
          fileGuid,
          request: {
            logPayload: (logPayload as Record<string, unknown>) || undefined,
          },
        });
        // Mark analysis as done regardless of outcome.
        setAutoAnalysingGuids((prev) => {
          const next = new Set(prev);
          next.delete(fileGuid);
          return next;
        });
        // If the analysis itself couldn't run (e.g. Bedrock not configured / timed
        // out), leave the row in autoFailedGuids without a stored result - the badge
        // falls back to "AI analysis pending" and the operator can retry from the modal.
        // A result that carries a proposed_config IS usable even when the model ended
        // its turn on the propose_config tool call with no closing text (empty
        // summary+details) - bailing here would skip the auto-apply below and strand
        // a valid fix as "issue detected".
        if (
          result.error ||
          (!result.summary && !result.details && !result.proposed_config)
        ) {
          return;
        }
        // Stash the result (incl. trace) so the row's [Review file analysis]
        // opens the modal instantly with the cached analysis - no re-fetch.
        setAutoAnalysisResults((prev) => {
          const next = new Map(prev);
          next.set(fileGuid, {
            summary: result.summary ?? "",
            details: result.details ?? "",
            proposed_config: result.proposed_config ?? null,
            proposed_config_rationale: result.proposed_config_rationale ?? null,
            trace: result.trace ?? null,
          });
          return next;
        });
        // Auto-apply the proposed fix ONLY on the first analysis for this file. If
        // we've already auto-applied a fix once (autoFixAttemptedGuids) and it failed,
        // we do NOT auto-apply again - the updated fix is surfaced for the operator to
        // apply manually via [Reprocess with proposed fix]. This keeps the operator in
        // control after the first attempt and bounds the loop.
        const alreadyAttempted = autoFixAttemptedGuids.has(fileGuid);
        if (
          !alreadyAttempted &&
          result.proposed_config &&
          typeof result.proposed_config === "object"
        ) {
          try {
            const reprocessResponse = await processHhDataTemp.mutateAsync({
              fileGuid,
              config: result.proposed_config as unknown as object,
              source: "ai_fix",
            });
            const newOperationKey = (
              reprocessResponse as unknown as { operationKey?: string }
            ).operationKey;
            if (newOperationKey) {
              setAutoFixAttemptedGuids((prev) => new Set(prev).add(fileGuid));
              setAutoOperationKeys((prev) => {
                const next = new Map(prev);
                next.set(fileGuid, newOperationKey);
                return next;
              });
              setAutoReprocessingGuids((prev) => {
                const next = new Set(prev);
                next.add(fileGuid);
                return next;
              });
            }
          } catch {
            // Auto-apply kickoff failed - leave in autoFailedGuids with stored
            // result so the operator sees [Review file analysis] +
            // [Reprocess with proposed fix].
          }
        }
      } catch {
        // Analysis itself failed (e.g. Bedrock not configured). Clear analysing
        // state - row will show the basic "AI analysis pending" state until
        // operator opens the modal to retry.
        setAutoAnalysingGuids((prev) => {
          const next = new Set(prev);
          next.delete(fileGuid);
          return next;
        });
      }
    },
    [fetchHhDataTempErrorAnalysis, processHhDataTemp, autoFixAttemptedGuids]
  );

  // While any Auto-process or auto-fix-reprocess LROs are in flight, poll /async_status
  // per operation. autoOperationKeys / autoProcessingGuids / autoReprocessingGuids are
  // read via refs so we don't re-bind the interval on every state change.
  const isAutoProcessing =
    autoProcessingGuids.size > 0 || autoReprocessingGuids.size > 0;
  const autoOperationKeysRef = useRef(autoOperationKeys);
  useEffect(() => {
    autoOperationKeysRef.current = autoOperationKeys;
  }, [autoOperationKeys]);
  const autoProcessingGuidsRef = useRef(autoProcessingGuids);
  useEffect(() => {
    autoProcessingGuidsRef.current = autoProcessingGuids;
  }, [autoProcessingGuids]);
  const autoReprocessingGuidsRef = useRef(autoReprocessingGuids);
  useEffect(() => {
    autoReprocessingGuidsRef.current = autoReprocessingGuids;
  }, [autoReprocessingGuids]);
  useEffect(() => {
    if (!isAutoProcessing) return;

    const startedAt = Date.now();
    const interval = setInterval(async () => {
      // Poll both the initial /process_auto LROs AND any in-flight auto-fix
      // /process reprocesses. We treat them identically for /async_status purposes
      // - completion clears the row, failure leaves it for AI analysis (or for
      // the failure path if analysis already happened).
      const inFlightGuids = new Set([
        ...autoProcessingGuidsRef.current,
        ...autoReprocessingGuidsRef.current,
      ]);
      const pairs = [...inFlightGuids]
        .map((guid) => {
          const operationKey = autoOperationKeysRef.current.get(guid);
          return operationKey ? { fileGuid: guid, operationKey } : null;
        })
        .filter(
          (p): p is { fileGuid: string; operationKey: string } => p !== null
        );

      const [listResult, statusResults] = await Promise.all([
        fetchHhDataTempList.refetch(),
        pairs.length > 0
          ? pollAutoOperationStatuses.mutateAsync(pairs)
          : Promise.resolve([]),
      ]);
      const files = (listResult.data?.files || []) as unknown as HhDataTempFile[];
      setSelectedTempFiles((prev) =>
        prev.map((p) => files.find((f) => f.fileGuid === p.fileGuid) || p)
      );

      const completedFromProcessing: string[] = [];
      const completedFromReprocessing: string[] = [];
      const failedFromProcessing: Array<{
        fileGuid: string;
        logPayload: unknown;
      }> = [];
      const failedFromReprocessing: Array<{
        fileGuid: string;
        logPayload: unknown;
      }> = [];
      for (const r of statusResults) {
        if (!r.status) continue;
        const isReprocess = autoReprocessingGuidsRef.current.has(r.fileGuid);
        const result = r.status.result as { logPayload?: unknown } | undefined;
        if (r.status.status === "completed") {
          if (isReprocess) completedFromReprocessing.push(r.fileGuid);
          else completedFromProcessing.push(r.fileGuid);
        } else if (r.status.status === "failed") {
          if (isReprocess) {
            // Auto-fix reprocess also failed - re-run the AI analysis. It's now
            // history-aware (the BE stored both the original + the auto-fix attempt
            // + their errors), so it won't re-propose the failed fix. fireAiAnalysis
            // won't auto-apply this time (autoFixAttemptedGuids is set), so the
            // updated fix is surfaced for the operator to apply manually.
            failedFromReprocessing.push({
              fileGuid: r.fileGuid,
              logPayload: result?.logPayload,
            });
          } else {
            failedFromProcessing.push({
              fileGuid: r.fileGuid,
              logPayload: result?.logPayload,
            });
          }
        }
      }

      // Initial-processing completion -> clear from autoProcessingGuids; file
      // should now have outputMetaData -> no badge.
      if (completedFromProcessing.length > 0) {
        setAutoProcessingGuids((prev) => {
          const next = new Set(prev);
          for (const g of completedFromProcessing) next.delete(g);
          return next;
        });
      }

      // Reprocess completion -> the AI auto-fix worked. Clear from autoReprocessing
      // AND autoFailedGuids (file is healed), and record it in autoFixedGuids so the
      // row shows the green "Auto-fixed with AI" badge.
      if (completedFromReprocessing.length > 0) {
        setAutoReprocessingGuids((prev) => {
          const next = new Set(prev);
          for (const g of completedFromReprocessing) next.delete(g);
          return next;
        });
        setAutoFailedGuids((prev) => {
          const next = new Set(prev);
          for (const g of completedFromReprocessing) next.delete(g);
          return next;
        });
        setAutoFixedGuids((prev) => {
          const next = new Set(prev);
          for (const g of completedFromReprocessing) next.add(g);
          return next;
        });
      }

      // Reprocess failure -> the auto-applied fix didn't work. Clear from
      // autoReprocessingGuids, then re-fire the (history-aware) AI analysis so it can
      // propose an UPDATED fix that avoids the one that just failed.
      if (failedFromReprocessing.length > 0) {
        setAutoReprocessingGuids((prev) => {
          const next = new Set(prev);
          for (const f of failedFromReprocessing) next.delete(f.fileGuid);
          return next;
        });
        setAutoAnalysingGuids((prev) => {
          const next = new Set(prev);
          for (const f of failedFromReprocessing) next.add(f.fileGuid);
          return next;
        });
        for (const f of failedFromReprocessing) {
          fireAiAnalysisAndAutoApply(f.fileGuid, f.logPayload);
        }
      }

      // Initial-processing failure -> move to autoFailedGuids + autoAnalysingGuids
      // and fire the AI analysis. On return, either auto-apply the proposed fix or
      // leave the cached analysis for the operator to review.
      if (failedFromProcessing.length > 0) {
        setAutoFailedGuids((prev) => {
          const newlyFailed = failedFromProcessing.filter(
            (f) => !prev.has(f.fileGuid)
          );
          if (newlyFailed.length > 0) {
            setAutoProcessingGuids((procPrev) => {
              const next = new Set(procPrev);
              for (const f of newlyFailed) next.delete(f.fileGuid);
              return next;
            });
            setAutoAnalysingGuids((anaPrev) => {
              const next = new Set(anaPrev);
              for (const f of newlyFailed) next.add(f.fileGuid);
              return next;
            });
            for (const f of newlyFailed) {
              fireAiAnalysisAndAutoApply(f.fileGuid, f.logPayload);
            }
          }
          const next = new Set(prev);
          for (const f of failedFromProcessing) next.add(f.fileGuid);
          return next;
        });
      }

      if (Date.now() - startedAt > 15 * 60 * 1000) {
        setAutoProcessingGuids(new Set());
        setAutoReprocessingGuids(new Set());
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [
    isAutoProcessing,
    fetchHhDataTempList,
    pollAutoOperationStatuses,
    fireAiAnalysisAndAutoApply,
  ]);

  // Inline "Reprocess with proposed fix" button on the row's failure badge. Pulls
  // the cached AI analysis result, fires /process with its proposed_config, and
  // moves the row into autoReprocessingGuids so the polling effect picks it up.
  const handleReprocessWithProposedFix = async (file: HhDataTempFile) => {
    const analysis = autoAnalysisResults.get(file.fileGuid);
    if (!analysis?.proposed_config) return;
    try {
      const response = await processHhDataTemp.mutateAsync({
        fileGuid: file.fileGuid,
        config: analysis.proposed_config as unknown as object,
        source: "ai_fix",
      });
      const newOperationKey = (
        response as unknown as { operationKey?: string }
      ).operationKey;
      if (!newOperationKey) return;
      setAutoOperationKeys((prev) => {
        const next = new Map(prev);
        next.set(file.fileGuid, newOperationKey);
        return next;
      });
      setAutoReprocessingGuids((prev) => {
        const next = new Set(prev);
        next.add(file.fileGuid);
        return next;
      });
    } catch (e: any) {
      alert(`Failed to start reprocess: ${e?.message || e}`);
    }
  };

  // [Review file analysis] from a row: open the modal showing the ALREADY-COMPUTED
  // analysis (incl. trace) instantly, instead of letting the modal re-run the
  // AI. Falls back to the normal Process flow if there's somehow no cached result.
  const handleReviewAnalysis = async (file: HhDataTempFile) => {
    const cached = autoAnalysisResults.get(file.fileGuid);
    setReviewAnalysis(cached ?? null);
    await handleProcessClick(file);
  };

  // Open the split-review modal for a multi-curve file.
  const handleReviewSplit = (file: HhDataTempFile) => {
    setSplitModalFile(file);
  };

  // Called by MultiCurveSplitModal once the split kicks off. Each new child guid
  // is now Auto-processing; register its operationKey into the same polling/coach
  // machinery the bulk run uses so the children get progress tracking AND the AI
  // auto-fix loop (the row-stacked children typically fail their first pass on
  // garbled dates and need the override_date_range coach fix). The source guid
  // drops out of the multi-curve set; the listing refetch removes its row.
  const handleSplitComplete = (
    sourceGuid: string,
    newGuids: { label: string; fileGuid: string; operationKey: string }[]
  ) => {
    if (newGuids.length > 0) {
      setAutoOperationKeys((prev) => {
        const next = new Map(prev);
        for (const g of newGuids) next.set(g.fileGuid, g.operationKey);
        return next;
      });
      setAutoProcessingGuids((prev) => {
        const next = new Set(prev);
        for (const g of newGuids) next.add(g.fileGuid);
        return next;
      });
      setAutoProcessingTotal((t) => t + newGuids.length);
    }
    setAutoMultiCurveGuids((prev) => {
      const next = new Set(prev);
      next.delete(sourceGuid);
      return next;
    });
    setAutoMultiCurveCandidates((prev) => {
      const next = new Map(prev);
      next.delete(sourceGuid);
      return next;
    });
    setSplitModalFile(null);
    fetchHhDataTempList.refetch();
  };

  const handleProcessClick = async (file: HhDataTempFile) => {
    setSelectedFileToProcess(file);
    setLoadingConfig(true);
    setShowProcessModal(true);
    // If this row's Auto-process failed during a bulk run, snapshot the operationKey
    // for the modal so useAsyncOperation can resume polling the failed LRO and surface
    // "Auto-fix with AI". Keep the badge in autoFailedGuids - it should persist until
    // the operator either (a) re-fires Auto-process from the bulk button (which clears
    // it explicitly) or (b) successfully reprocesses from within the modal (which lands
    // outputMetaData on the row, and the table's badge-gating hides the red badge).
    const isInFailedSet = autoFailedGuids.has(file.fileGuid);
    setBulkFailedOperationKey(
      isInFailedSet ? (autoOperationKeys.get(file.fileGuid) ?? null) : null
    );

    try {
      const configResponse = await fetchHhDataTempConfig.mutateAsync(
        file.fileGuid
      );
      setConfig(configResponse.config || {});
      if (configResponse.config !== null) {
        setConfig(configResponse.config);
      } else {
        // Fill in default config if none exist. A brand-new file (no config.json yet)
        // defaults to auto-detecting UTC-vs-local timestamps - the new behaviour for new
        // files (INRT-2372). Existing files load their stored config above and keep their
        // interpretation, so this only affects new uploads.
        setConfig({
          auto_detect_local_vs_utc: true,
          overrides: {
            fill_one_year_direction: "forwards",
          },
        });
      }
    } catch (error) {
      console.error("Error fetching config:", error);
      setConfig({});
    } finally {
      setLoadingConfig(false);
    }
  };

  const handleProcessComplete = async () => {
    // Refresh the file list to get the updated metadata for all files
    await fetchHhDataTempList.refetch();
  };

  // A process / auto-process was kicked off from inside the single-file modal.
  // Register the LRO into the same polling + AI-coach machinery the bulk flow uses
  // so the main table shows the spinner and resolves the outcome even after the
  // operator closes the modal. Clears any stale terminal state for the row first so
  // a re-run on a previously-failed/fixed row shows a clean in-progress spinner.
  const handleModalProcessingStarted = useCallback(
    (fileGuid: string, operationKey: string) => {
      setAutoOperationKeys((prev) => new Map(prev).set(fileGuid, operationKey));
      setAutoProcessingGuids((prev) => new Set(prev).add(fileGuid));
      setAutoProcessingTotal((t) => (t > 0 ? t : 1));
      const dropFromSet = (s: Set<string>) => {
        const next = new Set(s);
        next.delete(fileGuid);
        return next;
      };
      setAutoFailedGuids(dropFromSet);
      setAutoFixedGuids(dropFromSet);
      setAutoMultiCurveGuids(dropFromSet);
      setAutoAnalysingGuids(dropFromSet);
      setAutoReprocessingGuids(dropFromSet);
    },
    []
  );

  const handleBulkDeleteSelected = async () => {
    if (selectedTempFiles.length === 0) return;
    if (
      !window.confirm(
        `Delete ${selectedTempFiles.length} selected file(s)? This cannot be undone.`
      )
    ) {
      return;
    }
    setBulkDeleteInProgress(true);
    try {
      const results = await Promise.allSettled(
        selectedTempFiles.map((f) =>
          deleteHhDataTempFile.mutateAsync(f.fileGuid)
        )
      );
      const failures = results
        .map((r, i) => (r.status === "rejected" ? selectedTempFiles[i] : null))
        .filter((f): f is HhDataTempFile => f !== null);
      if (failures.length > 0) {
        alert(
          `Failed to delete ${failures.length} file(s):\n` +
            failures.map((f) => `- ${f.fileName}`).join("\n")
        );
      }
      await fetchHhDataTempList.refetch();
      setSelectedTempFiles([]);
    } finally {
      setBulkDeleteInProgress(false);
    }
  };

  const handleDeleteClick = async (file: HhDataTempFile) => {
    if (
      !window.confirm(
        `Are you sure you want to delete "${file.fileName}"? This action cannot be undone.`
      )
    ) {
      return;
    }

    try {
      await deleteHhDataTempFile.mutateAsync(file.fileGuid);
    } catch (error) {
      alert(`Error deleting file: ${error}`);
    }
  };

  const handleCommitClick = async (file: HhDataTempFile) => {
    if (
      !window.confirm(
        `Are you sure you want to commit "${file.fileName}"? This will save the processed data.`
      )
    ) {
      return;
    }

    try {
      await commitHhDataTemp.mutateAsync(file.fileGuid);
      await fetchHhDataList.refetch();
    } catch (error) {
      alert(`Error committing file: ${error}`);
    }
  };

  const handleEditClick = async (item: HhDataItem) => {
    if (
      !window.confirm(
        `Are you sure you want to edit "${item.fileName || item.guid}"? This will copy it back to temporary files for editing.`
      )
    ) {
      return;
    }

    try {
      await editHhData.mutateAsync(item.guid);
      await fetchHhDataTempList.refetch();
    } catch (error) {
      alert(`Error editing file: ${error}`);
    }
  };

  const handleCloseModal = () => {
    setShowProcessModal(false);
    setSelectedFileToProcess(null);
    setConfig({});
    setBulkFailedOperationKey(null);
    setReviewAnalysis(null);
  };

  const handleAssignToSitesClick = (item: HhDataItem) => {
    setSelectedHhDataItem(item);
    setShowAssignModal(true);
  };

  const handleCloseAssignModal = () => {
    setShowAssignModal(false);
    setSelectedHhDataItem(null);
  };

  const handleRemoveFromSitesClick = (item: HhDataItem) => {
    setSelectedHhDataItem(item);
    setShowRemoveModal(true);
  };

  const handleCloseRemoveModal = () => {
    setShowRemoveModal(false);
    setSelectedHhDataItem(null);
  };

  const handleViewConfigClick = (item: HhDataItem) => {
    setSelectedHhDataItem(item);
    setShowConfigModal(true);
  };

  const handleCloseConfigModal = () => {
    setShowConfigModal(false);
    setSelectedHhDataItem(null);
    if (searchParams.has("viewConfig")) {
      searchParams.delete("viewConfig");
      setSearchParams(searchParams, { replace: true });
    }
  };

  const handleDeleteHhDataClick = async (item: HhDataItem) => {
    if (
      !window.confirm(
        `Are you sure you want to delete "${item.fileName || item.guid}"? This action cannot be undone.`
      )
    ) {
      return;
    }

    try {
      await deleteHhDataFile.mutateAsync(item.guid);
    } catch (error) {
      alert(`Error deleting file: ${error}`);
    }
  };

  // Auto-open View Config modal when viewConfig URL param is present
  const viewConfigHandled = useRef(false);
  useEffect(() => {
    if (
      viewConfigGuid &&
      !viewConfigHandled.current &&
      fetchHhDataList.data?.hhData
    ) {
      const item = fetchHhDataList.data.hhData.find(
        (i) => i.guid === viewConfigGuid
      );
      if (item) {
        viewConfigHandled.current = true;
        setSelectedHhDataItem(item);
        setShowConfigModal(true);
      }
    }
  }, [viewConfigGuid, fetchHhDataList.data]);

  // Prune the bulk-action selections when a row leaves its list (deleted, committed,
  // or split away). Without this the selection kept a stale reference, so after
  // deleting the only uploaded file the row vanished but the Auto-process / Delete bulk
  // actions still counted "1 file selected". Keyed on the list data so it runs on every
  // refetch; returns the previous array unchanged when nothing was pruned to avoid an
  // extra render.
  useEffect(() => {
    const liveGuids = new Set(
      ((fetchHhDataTempList.data?.files || []) as unknown as HhDataTempFile[]).map(
        (f) => f.fileGuid
      )
    );
    setSelectedTempFiles((prev) => {
      const pruned = prev.filter((f) => liveGuids.has(f.fileGuid));
      return pruned.length === prev.length ? prev : pruned;
    });
  }, [fetchHhDataTempList.data]);

  useEffect(() => {
    const liveGuids = new Set(
      (fetchHhDataList.data?.hhData || []).map((i) => i.guid)
    );
    setSelectedHhDataItems((prev) => {
      const pruned = prev.filter((i) => liveGuids.has(i.guid));
      return pruned.length === prev.length ? prev : pruned;
    });
  }, [fetchHhDataList.data]);

  const siteMap = useMemo(() => {
    if (!fetchHhDataList.data) return new Map<string, SiteDemandView>();
    const mappedSites = fetchHhDataList.data.hhData
      .flatMap((item) => item.siteMappings)
      .map((siteMapping) => siteMapping.site)
      .reduce(
        (acc, curr) => acc.set(curr.id, curr),
        new Map<string, SiteDemandView>()
      );
    for (const site of fetchHhDataList.data.availableSites) {
      if (!mappedSites.has(site.id)) {
        mappedSites.set(site.id, site);
      }
    }
    return mappedSites;
  }, [fetchHhDataList.data]);

  if (fetchHhDataTempList.isLoading || fetchHhDataList.isLoading) {
    return <Loading label="Loading demand data files..." />;
  }

  if (fetchHhDataTempList.isError || fetchHhDataList.isError) {
    return <div>Error loading demand data files.</div>;
  }

  const files = (fetchHhDataTempList.data?.files || []).sort(
    reversed(sortBy((file: HhDataTempFile) => file.uploadedAt))
  );

  const processedSelectedFiles = selectedTempFiles.filter(
    (f) => !!f.outputMetaData
  );
  const unprocessedSelectedFiles = selectedTempFiles.filter(
    (f) => !f.outputMetaData
  );

  const hhDataItems = (fetchHhDataList.data?.hhData || []).sort(
    reversed(sortBy((item: HhDataItem) => item.processedTime || 0))
  );

  return (
    <Container fluid style={{ padding: "12px 12px 0px 12px" }}>
      <h2>Demand Data Management</h2>

      <Card body className="mt-3">
        <h4>Demand input</h4>
        <Row>
          <Col md={6}>
            <h5>Upload files</h5>
            <Form.Group className="mb-3">
              <Form.Label>Select one or more files</Form.Label>
              <Form.Control
                id="file-upload"
                type="file"
                multiple
                onChange={handleFileSelect}
                accept=".csv,.xlsx,.xls"
              />
            </Form.Group>
            {selectedFiles.length > 0 && (
              <div className="mb-3">
                <div className="small text-muted mb-1">
                  {selectedFiles.length} file(s) queued:
                </div>
                <div className="d-flex flex-wrap gap-2">
                  {selectedFiles.map((f, i) => (
                    <span
                      key={i}
                      className="badge bg-light text-dark border d-flex align-items-center gap-1"
                      style={{ maxWidth: 280 }}
                    >
                      <span
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {f.name}
                      </span>
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 text-danger"
                        onClick={() => handleRemoveSelectedFile(i)}
                        disabled={uploadInProgress}
                      >
                        ×
                      </Button>
                    </span>
                  ))}
                </div>
              </div>
            )}
            <Button
              variant="primary"
              onClick={handleUpload}
              disabled={selectedFiles.length === 0 || uploadInProgress}
            >
              {uploadInProgress && uploadProgress
                ? `Uploading ${uploadProgress.current + 1} of ${uploadProgress.total}...`
                : selectedFiles.length === 0
                  ? "Upload"
                  : `Upload ${selectedFiles.length} file(s)`}
            </Button>
          </Col>
          <Col md={6}>
            <h5>Load site actual demand</h5>
            <DemandDataLoadSiteActuals
              onExtractComplete={() => fetchHhDataTempList.refetch()}
            />
          </Col>
        </Row>
      </Card>

      <Card body className="mt-3">
        <div className="d-flex justify-content-between align-items-center">
          <h4 className="mb-0">Files available to process ({files.length})</h4>
          {selectedTempFiles.length > 0 && (
            <div className="d-flex gap-2 align-items-center">
              {isAutoProcessing && (
                <span className="small text-muted">
                  Auto-processing:{" "}
                  {autoProcessingTotal - autoProcessingGuids.size} of{" "}
                  {autoProcessingTotal} completed...
                </span>
              )}
              {unprocessedSelectedFiles.length > 0 && (
                <Button
                  variant="primary"
                  size="sm"
                  disabled={isAutoProcessing}
                  onClick={handleAutoProcessSelected}
                >
                  Auto-process {unprocessedSelectedFiles.length} file
                  {unprocessedSelectedFiles.length === 1 ? "" : "s"}
                </Button>
              )}
              {processedSelectedFiles.length > 0 && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setShowMatchToSitesModal(true)}
                >
                  Match {processedSelectedFiles.length} to sites...
                </Button>
              )}
              <Button
                variant="outline-danger"
                size="sm"
                disabled={isAutoProcessing || bulkDeleteInProgress}
                onClick={handleBulkDeleteSelected}
              >
                {bulkDeleteInProgress ? (
                  <>
                    <Spinner
                      animation="border"
                      size="sm"
                      className="me-1"
                    />
                    Deleting {selectedTempFiles.length} file
                    {selectedTempFiles.length === 1 ? "" : "s"}...
                  </>
                ) : (
                  <>
                    Delete {selectedTempFiles.length} file
                    {selectedTempFiles.length === 1 ? "" : "s"}
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
        <div style={{ fontSize: "14px" }}>
          (Uncommitted files are deleted after 7 days)
        </div>
        {files.length > 0 ? (
          <FileSummaryTable
            files={files}
            showActions={true}
            onProcessClick={handleProcessClick}
            onDeleteClick={handleDeleteClick}
            onCommitClick={handleCommitClick}
            selectedFiles={selectedTempFiles}
            onSelectionChange={setSelectedTempFiles}
            autoProcessingGuids={autoProcessingGuids}
            autoFailedGuids={autoFailedGuids}
            autoAnalysingGuids={autoAnalysingGuids}
            autoReprocessingGuids={autoReprocessingGuids}
            autoAnalysisResults={autoAnalysisResults}
            autoFixAttemptedGuids={autoFixAttemptedGuids}
            autoFixedGuids={autoFixedGuids}
            autoAttemptCounts={autoAttemptCounts}
            autoMultiCurveGuids={autoMultiCurveGuids}
            onReviewAnalysis={handleReviewAnalysis}
            onReprocessWithProposedFix={handleReprocessWithProposedFix}
            onReviewSplit={handleReviewSplit}
          />
        ) : (
          <p className="mt-2">No files uploaded yet.</p>
        )}
      </Card>

      <GraphDemandData
        selectedTempFiles={selectedTempFiles}
        selectedHhDataItems={selectedHhDataItems}
        siteMap={siteMap}
        onClearSelection={() => {
          setSelectedTempFiles([]);
          setSelectedHhDataItems([]);
        }}
      />

      <Card body className="mt-3">
        <div className="d-flex justify-content-between align-items-center">
          <h4 className="mb-0">
            Processed demand data (
            {selectedHhDataItems.length > 0
              ? `${selectedHhDataItems.length} of ${hhDataItems.length} selected`
              : hhDataItems.length}
            )
          </h4>
          {selectedHhDataItems.length > 0 && (
            <Button
              variant="outline-primary"
              size="sm"
              disabled={downloadHhDataZip.isLoading}
              onClick={async () => {
                try {
                  const result = await downloadHhDataZip.mutateAsync({
                    guids: selectedHhDataItems.map((i) => i.guid),
                  });
                  window.open(result.downloadUrl, "_blank");
                } catch (e: any) {
                  alert(
                    `Download failed: ${e?.response?.data?.error || e?.message || e}`
                  );
                }
              }}
            >
              {downloadHhDataZip.isLoading
                ? "Building zip..."
                : `Download ${selectedHhDataItems.length} as zip`}
            </Button>
          )}
        </div>
        {hhDataItems.length > 0 ? (
          <HhDataTable
            items={hhDataItems}
            siteMap={siteMap}
            showActions={true}
            onEditClick={handleEditClick}
            onViewConfigClick={handleViewConfigClick}
            onAssignToSitesClick={handleAssignToSitesClick}
            onRemoveFromSitesClick={handleRemoveFromSitesClick}
            onDeleteClick={handleDeleteHhDataClick}
            onReviewAnalysisClick={setReviewAnalysisItem}
            selectedItems={selectedHhDataItems}
            onSelectionChange={setSelectedHhDataItems}
            initialFilterText={initialFilterText}
          />
        ) : (
          <p className="mt-2">No committed data yet.</p>
        )}
      </Card>

      {showProcessModal && selectedFileToProcess && (
        <DemandFileProcessModal
          file={selectedFileToProcess}
          config={config}
          loadingConfig={loadingConfig}
          onClose={handleCloseModal}
          onProcessComplete={handleProcessComplete}
          initialOperationKey={bulkFailedOperationKey}
          initialErrorAnalysis={reviewAnalysis}
          onProcessingStarted={handleModalProcessingStarted}
        />
      )}

      {splitModalFile &&
        autoMultiCurveCandidates.has(splitModalFile.fileGuid) && (
          <MultiCurveSplitModal
            file={splitModalFile}
            candidates={
              autoMultiCurveCandidates.get(splitModalFile.fileGuid)!.candidates
            }
            reason={
              autoMultiCurveCandidates.get(splitModalFile.fileGuid)!.reason
            }
            onClose={() => setSplitModalFile(null)}
            onSplitComplete={(newGuids) =>
              handleSplitComplete(splitModalFile.fileGuid, newGuids)
            }
          />
        )}

      {showAssignModal && selectedHhDataItem && (
        <DemandDataSiteAssignment
          hhDataItem={selectedHhDataItem}
          siteMap={siteMap}
          onClose={handleCloseAssignModal}
        />
      )}

      {showRemoveModal && selectedHhDataItem && (
        <DemandDataSiteRemoval
          hhDataItem={selectedHhDataItem}
          onClose={handleCloseRemoveModal}
        />
      )}

      {showConfigModal && selectedHhDataItem && (
        <DemandFileConfigModal
          item={selectedHhDataItem}
          onClose={handleCloseConfigModal}
        />
      )}

      {reviewAnalysisItem && (
        <DemandFileAnalysisReviewModal
          item={reviewAnalysisItem}
          onClose={() => setReviewAnalysisItem(null)}
        />
      )}

      {showMatchToSitesModal && (
        <MatchToSitesModal
          files={processedSelectedFiles}
          onClose={() => {
            setShowMatchToSitesModal(false);
            setSelectedTempFiles([]);
            fetchHhDataTempList.refetch();
            fetchHhDataList.refetch();
          }}
        />
      )}
    </Container>
  );
};

export default DemandData;
